import { forIn, filter, map, assign, cloneDeep } from "lodash-es";
import $ from "jquery";

export default {
    videoStack: {},
    audioStack: {},

    videoJSOptions: {
        controls: true,
        autoplay: false,
        loop: false,
        width: 600,
        height: 360,
        forceSSL: true,
        playsinline: 1,
        origin: window.location.origin,
        techOrder: ["html5", "youtube", "vimeo", "wistia"],
        html5: {
            nativeTextTracks: false,
        },
    },

    playerBreakpoints: {
        L: { size: 720, name: "vjs-large-player" },
        M: { size: 358, name: "vjs-medium-player" },
        S: { size: 248, name: "vjs-small-player" },
        XS: { size: 195, name: "vjs-xsmall-player" },
    },

    _vjsEncode(data, decode = false) {
        if (!data) return;

        if (decode) {
            return JSON.parse(decodeURIComponent(data));
        }
        return encodeURIComponent(JSON.stringify(data));
    },

    _getSettings($el, defaultOptions) {
        const markersOptions = $el.data("markers");
        const fullscreenDisabled = $el.data("fullscreen-disabled");
        const inactivityDisabled = $el.data("inactivity-disabled");
        const customOptions = this._vjsEncode($el.data("custom-options"), true);

        let options;
        if (customOptions) {
            options = assign({}, this.videoJSOptions, customOptions);
        } else {
            options = defaultOptions;
        }
        options = cloneDeep(options);

        return {
            markersOptions,
            fullscreenDisabled,
            inactivityDisabled,
            options,
            customOptions: !!customOptions,
        };
    },

    _getOptionsPoster({ options, poster, container, media, filestack }) {
        let changeToPNG = false;
        if (poster) {
            let posterObject = poster.desktop;

            const isDesktop = (container && container.hasClass("kio-widget-container")) || !container;
            if (!isDesktop) {
                posterObject = poster.mobile || poster.desktop;
            }

            changeToPNG = posterObject && ["tiff", "tif"].includes(posterObject.type);

            options.poster = posterObject && posterObject.url;
        }

        if (options.poster && filestack && media.type.search("audio") >= 0) {
            const imageSize = {
                width: 160,
                height: 160,
            };
            options.poster = this._getFilestackURL({ url: options.poster, imageSize, filestack, changeToPNG });
        } else if (!options.poster && media && media.thumbnail) {
            options.poster = media.thumbnail;
        } else if (options.poster && changeToPNG) {
            const imageSize = {
                width: 816,
                height: 469,
            };
            options.poster = this._getFilestackURL({ url: options.poster, imageSize, filestack, changeToPNG });
        }

        return options;
    },

    _getFilestackURL({ url, imageSize = { width: 960, height: 960 }, filestack, changeToPNG = false }) {
        if (url.search("cdn.filestack") >= 0 && filestack) {
            const resize = `resize=w:${imageSize.width},h:${imageSize.height},a:center/`;
            const changeToPNGString = changeToPNG ? "output=format:png/" : "";
            url = `https://process.filestackapi.com/${filestack}/compress/${resize}/${changeToPNGString}${url}`;
        }
        return url;
    },

    _getPlayerSize(width) {
        return map(
            filter(this.playerBreakpoints, breakpoint => {
                return width >= breakpoint.size;
            }),
            "name",
        ).join(" ");
    },

    _playerResize(player, cb) {
        const $player = $(player.el());

        // Width standards for media elements in widget RT inputs
        // const $closestRichContent = $player.closest(`.${themeClasses.richContent}`);
        const $closestRichContent = $player.closest(".kiot-rich-content");
        if ($closestRichContent.get(0)) {
            const closestRichContentWidth = $closestRichContent.width();
            const $playerParent = $player.parent();
            const prevStyle = $playerParent.attr("data-previous-style");
            if (closestRichContentWidth < 480) {
                const newPrevStyle = $playerParent.attr("style");
                $playerParent.css("width", "100%");
                if (!prevStyle) {
                    $playerParent.attr("data-previous-style", newPrevStyle);
                }
            } else if (prevStyle) {
                $playerParent.attr("style", prevStyle);
            }
        }

        const playerWidth = $player.width();
        const playerSize = this._getPlayerSize(playerWidth);
        $player.attr("data-player-size", playerSize);

        const $captionButton = $player.find(".vjs-captions-button.vjs-control.vjs-button");
        if (playerSize === "vjs-xsmall-player") {
            $captionButton.css({
                left: `${32 + 8 + 8 - playerWidth}px`,
                position: "absolute",
                top: 0,
            });
        } else {
            $captionButton.css("left", null);
        }

        if (cb) {
            cb(player);
        }
    },

    _pauseMedias(notPauseID) {
        forIn(this.videoStack, (value, key) => {
            if (!notPauseID || (notPauseID && key !== notPauseID)) {
                try {
                    if (!this.videoStack[key].paused()) {
                        this.videoStack[key].pause();
                    }
                } catch (e) {
                    /* Catching paused() VideoJS error on HTML5 players */
                }
            }
        });

        forIn(this.audioStack, (value, key) => {
            if (!notPauseID || (notPauseID && key !== notPauseID)) {
                try {
                    if (!this.audioStack[key].paused()) {
                        this.audioStack[key].pause();
                    }
                } catch (e) {
                    /* Catching paused() VideoJS error on HTML5 players */
                }
            }
        });
    },

    _extraClasses(player, isEdit, fromFroala) {
        const $player = $(player.el());

        if (isEdit) {
            if (fromFroala) {
                $player.addClass("in-froala");
            }
        }

        player.on("ready", () => {
            $player.addClass("is-ready");
        });
    },

    async _disposePlayer({ videojs, player }) {
        player.dispose();

        const id = player.id();
        delete this.videoStack[id];
        delete this.audioStack[id];
        delete videojs.players[id];
    },

    _initMarkers(player, markersOptions) {
        if (markersOptions) {
            player.markers(
                Object.assign(markersOptions, {
                    onMarkerReached: (marker, index) => {
                        player.trigger("markerReached", { marker, index });
                    },
                    onMarkerHover: (marker, index) => {
                        player.trigger("markerHover", { marker, index });
                    },
                    onMarkerOut: (marker, index) => {
                        player.trigger("markerOut", { marker, index });
                    },
                }),
            );
        }
    },
};
